import { api, handleApiError } from './apiConfig';

// Quiz API service with optimized error handling
export const quizApi = {
  // Get all available quizzes for a user
  getAllQuizzes: async (email) => {
    try {
      const response = await api.get(`/quiz/all?email=${email}`);
      
      // Normalize the completion status for all quizzes
      if (response.data) {
        // Process daily quizzes
        if (response.data.daily_quizzes && Array.isArray(response.data.daily_quizzes)) {
          response.data.daily_quizzes = response.data.daily_quizzes.map(quiz => ({
            ...quiz,
            // Ensure completed is a boolean
            completed: Boolean(quiz.completed || (quiz.score !== undefined && quiz.score !== null))
          }));
        }
        
        // Process milestone quizzes
        if (response.data.milestone_quizzes && Array.isArray(response.data.milestone_quizzes)) {
          response.data.milestone_quizzes = response.data.milestone_quizzes.map(quiz => ({
            ...quiz,
            // Ensure completed is a boolean
            completed: Boolean(quiz.completed || (quiz.score !== undefined && quiz.score !== null))
          }));
        }
      }
      
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to fetch all quizzes');
      throw new Error(errorMessage);
    }
  },
  
  // Get a daily letter quiz by letter ID
  getDailyQuiz: async (letterId, email) => {
    try {
      const response = await api.get(`/quiz/${letterId}?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to fetch daily quiz');
      throw new Error(errorMessage);
    }
  },
  
  // Check if a daily letter quiz has been completed
  checkDailyQuizCompleted: async (letterId, email) => {
    try {
      const response = await api.get(`/quiz/daily/${letterId}/completed?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to check quiz completion status');
      throw new Error(errorMessage);
    }
  },

  // Get a specific quiz by ID
  getQuiz: async (quizId, email) => {
    try {
      const response = await api.get(`/quiz/${quizId}?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to fetch quiz');
      throw new Error(errorMessage);
    }
  },

  // Submit quiz answers for milestone quizzes
  submitQuiz: async (quizId, answers, email, timeTaken = 0) => {
    try {
      const response = await api.post(`/quiz/submit?email=${encodeURIComponent(email)}`, {
        quiz_id: quizId,
        answers: answers,
        time_taken: timeTaken // Include time taken for consistency with daily quiz
      });
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to submit quiz');
      throw new Error(errorMessage);
    }
  },

  // Submit daily letter quiz answers
  submitDailyQuiz: async (letterId, answers, email, timeTaken) => {
    try {
      const response = await api.post(`/quiz/submit?email=${encodeURIComponent(email)}`, {
        quiz_id: letterId.toString(), // Convert to string to ensure consistency
        answers: answers,
        time_taken: timeTaken || 0 // Include time taken for analytics
      });
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to submit daily quiz');
      throw new Error(errorMessage);
    }
  },

  // Get user's quiz history
  getQuizHistory: async (email) => {
    try {
      const response = await api.get(`/quiz/history?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to fetch quiz history');
      throw new Error(errorMessage);
    }
  },

  // Get results for a specific quiz
  getQuizResults: async (quizId, email) => {
    try {
      const response = await api.get(`/quiz/results/${quizId}?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to fetch quiz results');
      throw new Error(errorMessage);
    }
  },

  // Get user quiz history for a specific quiz
  getUserQuizHistory: async (quizId, email) => {
    try {
      const response = await api.get(`/quiz/history/${quizId}?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to fetch quiz history');
      throw new Error(errorMessage);
    }
  },
};

export default quizApi;
